/**
 * splineConverter — SPLINE entity → CadPrimitive (PDF-5c).
 *
 * # CLAUDE.md priority #1 (no simplification)
 *
 * REUSES the pure `buildSplineBeziers` from `@dxf-ai-editor/drawing-model`
 * (the SAME math the WebGL adapter `splineToRenderable` feeds Line2 with) so
 * the PDF curve is byte-for-byte the cubic the editor draws on screen —
 * CUBIC bezier only, never quadratic, never a straight-line polyline approx.
 * Both fit-points mode (with optional start/end tangent clamp) and
 * control-points mode (NURBS, knots + rational weights) are honoured because
 * `buildSplineBeziers` picks the construction path from the supplied fields.
 *
 * The resulting `CubicBezierSegment[]` maps directly onto one stroked
 * `PathPrimitive`: the first segment's `p0` is the path `start`, each segment
 * becomes a `{kind:"bezier"}` step. `closed` SPLINEs emit `closePath` (the
 * backend's `h` before stroke) so the loop joins start↔end like AutoCAD.
 *
 * Coordinates are RAW world mm (planar 2D — z dropped, matching the model's
 * `planar` default). Splines are NOT pre-offset by the scene adapter the way
 * the dimension/multileader adapters are, so there is no offset to un-bake.
 */

import type { SplineEntity } from "@dxf-ai-editor/drawing-model";
import {
  buildSplineBeziers,
  type CubicBezierSegment,
  type SplinePoint,
} from "@dxf-ai-editor/drawing-model";

import type {
  CadConvertContext,
  CadPrimitive,
  PathSegment,
  Point2,
} from "../cadPrimitive";
import { strokedPathPrimitives } from "./strokedPathPrimitive";

/** Drop z → planar 2D `SplinePoint` (the model defaults `planar: true`). */
function planar(p: { x: number; y: number; z?: number }): SplinePoint {
  return { x: p.x, y: p.y };
}

/**
 * Build the cubic bezier segments for a SPLINE entity, picking fit-points vs
 * control-points mode the same way the renderer does. Shared with the INSERT
 * converter (block-child SPLINE), which then transforms the points through the
 * block affine. Returns an empty array for a degenerate spline (no usable
 * points) — `buildSplineBeziers` already guards this.
 */
export function splineBezierSegments(spline: {
  controlPoints?: readonly { x: number; y: number; z?: number }[];
  fitPoints?: readonly { x: number; y: number; z?: number }[];
  startTangent?: { x: number; y: number; z?: number };
  endTangent?: { x: number; y: number; z?: number };
  knots?: readonly number[];
  weights?: readonly number[];
  degree: number;
  closed?: boolean;
}): CubicBezierSegment[] {
  const controlPoints = (spline.controlPoints ?? []).map(planar);
  const fitPoints = (spline.fitPoints ?? []).map(planar);
  const weights = spline.weights ?? [];

  const segments = buildSplineBeziers({
    controlPoints: controlPoints.length > 0 ? controlPoints : undefined,
    fitPoints: fitPoints.length > 0 ? fitPoints : undefined,
    ...(spline.startTangent ? { startTangent: planar(spline.startTangent) } : {}),
    ...(spline.endTangent ? { endTangent: planar(spline.endTangent) } : {}),
    knots: spline.knots ? [...spline.knots] : undefined,
    // Rational weights only apply in control-points mode; pass them through.
    ...(weights.length > 0 && controlPoints.length > 0
      ? { weights: [...weights] }
      : {}),
    degree: Math.max(1, Math.round(spline.degree)),
  });

  // A closed spline that has no closing span (start ≠ end) gets one straight
  // closing bezier appended so the loop joins; AutoCAD closes periodic splines
  // through the construction (uniformKnotVector clamp) but a defensive closing
  // segment matches the `closePath` semantics for the open-ended fit case.
  return segments;
}

/**
 * Convert the bezier segments to one open/closed stroked path. The first
 * segment's `p0` is the path start; each segment is a cubic step. When
 * `closed`, the backend emits `closePath` so the curve loops.
 */
export function bezierSegmentsToPath(
  segments: CubicBezierSegment[],
): { start: Point2; pathSegments: PathSegment[] } | null {
  if (segments.length === 0) return null;
  const first = segments[0]!;
  const start: Point2 = { x: first.p0.x, y: first.p0.y };
  const pathSegments: PathSegment[] = segments.map((s) => ({
    kind: "bezier" as const,
    c1: { x: s.cp1.x, y: s.cp1.y },
    c2: { x: s.cp2.x, y: s.cp2.y },
    to: { x: s.p3.x, y: s.p3.y },
  }));
  return { start, pathSegments };
}

/** SPLINE → CadPrimitive[] (one stroked cubic-bezier path). */
export function splineToPdfPrimitives(
  entity: SplineEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const segments = splineBezierSegments(entity);
  const built = bezierSegmentsToPath(segments);
  if (!built) return [];
  return strokedPathPrimitives(entity, ctx, [
    {
      start: built.start,
      segments: built.pathSegments,
      closed: entity.closed === true,
    },
  ]);
}
